/**
 * UploadDropzone 檔案上傳元件
 *
 * 提供拖放檔案上傳功能：
 * - 拖放區域
 * - 點擊選擇檔案
 * - 檔案類型驗證
 * - 預覽功能
 */

import { resizeImage, fileToBase64 } from '@/utils/file';
import { isValidUrl } from '@/utils/security';

// ========================================================================
// 型別定義
// ========================================================================

export interface UploadDropzoneOptions {
  /** 接受的檔案類型 */
  accept?: string;
  /** 最大檔案大小（位元組） */
  maxSize?: number;
  /** 是否允許多選 */
  multiple?: boolean;
  /** 是否自動調整圖片大小 */
  autoResize?: boolean;
  /** 調整後的最大寬度 */
  resizeMaxWidth?: number;
  /** 調整後的最大高度 */
  resizeMaxHeight?: number;
  /** 檔案選擇回呼 */
  onFileSelect?: (file: File, base64: string) => void;
  /** 錯誤回呼 */
  onError?: (error: string) => void;
}

// ========================================================================
// UploadDropzone 類別
// ========================================================================

export class UploadDropzone {
  private element: HTMLDivElement;
  private input: HTMLInputElement;
  private preview: HTMLDivElement | null = null;
  private options: Required<Omit<UploadDropzoneOptions, 'onFileSelect' | 'onError'>> & Pick<UploadDropzoneOptions, 'onFileSelect' | 'onError'>;
  private isDragOver: boolean = false;
  private selectedFile: File | null = null;

  constructor(options: UploadDropzoneOptions = {}) {
    this.options = {
      accept: options.accept ?? 'image/*',
      maxSize: options.maxSize ?? 10 * 1024 * 1024, // 10MB
      multiple: options.multiple ?? false,
      autoResize: options.autoResize ?? true,
      resizeMaxWidth: options.resizeMaxWidth ?? 2000,
      resizeMaxHeight: options.resizeMaxHeight ?? 2000,
      onFileSelect: options.onFileSelect,
      onError: options.onError,
    };

    this.element = this.createElement();
    this.input = this.createInput();
    this.element.appendChild(this.input);

    this.bindEvents();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  getElement(): HTMLDivElement {
    return this.element;
  }

  getSelectedFile(): File | null {
    return this.selectedFile;
  }

  /**
   * 重置上傳區域
   */
  reset(): void {
    this.selectedFile = null;
    this.input.value = '';
    this.hidePreview();
    this.showDropzone();
  }

  /**
   * 顯示預覽圖
   * 🔒 安全性：驗證 URL，但允許安全的 data: URL（僅限圖片）
   */
  showPreview(imageUrl: string): void {
    if (!imageUrl) {
      console.error('[Security] Empty preview URL');
      this.options.onError?.('無效的圖片 URL');
      return;
    }

    // 🔒 安全性檢查：
    // 1. 允許圖片類型的 data: URL（由應用自己生成，用於預覽）
    // 2. 其他 URL 必須通過 isValidUrl() 檢查
    const isDataUrl = imageUrl.startsWith('data:');
    if (isDataUrl) {
      // 驗證是否為圖片類型的 data URL
      const isImageDataUrl = /^data:image\/(png|jpeg|jpg|gif|webp);base64,/.test(imageUrl);
      if (!isImageDataUrl) {
        console.error('[Security] Invalid data URL format (must be image/*)', imageUrl.substring(0, 50));
        this.options.onError?.('無效的圖片格式');
        return;
      }
    } else {
      // 非 data URL 需要通過一般的 URL 驗證
      if (!isValidUrl(imageUrl)) {
        console.error('[Security] Invalid or unsafe preview URL blocked:', imageUrl);
        this.options.onError?.('無效的圖片 URL');
        return;
      }
    }

    if (!this.preview) {
      this.preview = document.createElement('div');
      this.preview.className = 'relative';
      this.element.parentElement?.appendChild(this.preview);
    }

    this.preview.innerHTML = `
      <div class="relative rounded-lg overflow-hidden border border-gray-200">
        <img src="${imageUrl}" alt="預覽" class="w-full h-auto max-h-64 object-contain bg-gray-100">
        <button type="button" class="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
          <svg class="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `;

    const clearBtn = this.preview.querySelector('button');
    clearBtn?.addEventListener('click', () => this.reset());

    this.element.classList.add('hidden');
    this.preview.classList.remove('hidden');
  }

  /**
   * 隱藏預覽圖
   */
  hidePreview(): void {
    this.preview?.classList.add('hidden');
  }

  /**
   * 顯示上傳區域
   */
  showDropzone(): void {
    this.element.classList.remove('hidden');
  }

  /**
   * 掛載到容器
   */
  mount(container: HTMLElement): void {
    container.appendChild(this.element);
    if (this.preview) {
      container.appendChild(this.preview);
    }
  }

  /**
   * 銷毀元件
   */
  destroy(): void {
    this.element.remove();
    this.preview?.remove();
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  private createElement(): HTMLDivElement {
    const element = document.createElement('div');
    element.className = [
      'upload-dropzone',
      'border-2',
      'border-dashed',
      'border-gray-300',
      'rounded-lg',
      'p-8',
      'text-center',
      'cursor-pointer',
      'transition-colors',
      'hover:border-primary-400',
      'hover:bg-primary-50',
    ].join(' ');

    element.innerHTML = `
      <div class="flex flex-col items-center">
        <svg class="w-12 h-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
        </svg>
        <p class="text-gray-600 mb-2">
          <span class="font-medium text-primary-600">點擊上傳</span>
          或拖放檔案至此
        </p>
        <p class="text-sm text-gray-500">
          支援 ${this.options.accept.replace('image/*', 'PNG, JPG')}
        </p>
      </div>
    `;

    return element;
  }

  private createInput(): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = this.options.accept;
    input.multiple = this.options.multiple;
    input.className = 'hidden';
    return input;
  }

  private bindEvents(): void {
    // 點擊觸發選擇檔案
    this.element.addEventListener('click', () => this.input.click());

    // 檔案選擇
    this.input.addEventListener('change', () => {
      const file = this.input.files?.[0];
      if (file) {
        this.handleFile(file);
      }
    });

    // 拖放事件
    this.element.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!this.isDragOver) {
        this.isDragOver = true;
        this.element.classList.add('border-primary-500', 'bg-primary-50');
      }
    });

    this.element.addEventListener('dragleave', (e) => {
      e.preventDefault();
      this.isDragOver = false;
      this.element.classList.remove('border-primary-500', 'bg-primary-50');
    });

    this.element.addEventListener('drop', (e) => {
      e.preventDefault();
      this.isDragOver = false;
      this.element.classList.remove('border-primary-500', 'bg-primary-50');

      const file = e.dataTransfer?.files[0];
      if (file) {
        this.handleFile(file);
      }
    });
  }

  private async handleFile(file: File): Promise<void> {
    // 驗證檔案類型
    if (!this.validateFileType(file)) {
      this.options.onError?.(`不支援的檔案類型：${file.type}`);
      return;
    }

    // 驗證檔案大小
    if (file.size > this.options.maxSize) {
      const maxSizeMB = (this.options.maxSize / 1024 / 1024).toFixed(1);
      this.options.onError?.(`檔案大小超過限制（最大 ${maxSizeMB}MB）`);
      return;
    }

    this.selectedFile = file;

    try {
      let base64: string;

      if (this.options.autoResize && file.type.startsWith('image/')) {
        // 調整圖片大小
        const resized = await resizeImage(file, this.options.resizeMaxWidth);
        base64 = resized.dataUrl;
      } else {
        base64 = await fileToBase64(file);
      }

      // 顯示預覽
      this.showPreview(base64);

      // 觸發回呼
      this.options.onFileSelect?.(file, base64);
    } catch (error) {
      this.options.onError?.('處理檔案時發生錯誤');
      console.error('File processing error:', error);
    }
  }

  private validateFileType(file: File): boolean {
    const accept = this.options.accept;

    if (accept === '*' || accept === '*/*') {
      return true;
    }

    const acceptedTypes = accept.split(',').map((t) => t.trim());

    for (const type of acceptedTypes) {
      if (type.endsWith('/*')) {
        // 如 'image/*'
        const category = type.slice(0, -2);
        if (file.type.startsWith(category)) {
          return true;
        }
      } else if (type.startsWith('.')) {
        // 如 '.png'
        if (file.name.toLowerCase().endsWith(type.toLowerCase())) {
          return true;
        }
      } else {
        // 如 'image/png'
        if (file.type === type) {
          return true;
        }
      }
    }

    return false;
  }
}

// ========================================================================
// 工廠函式
// ========================================================================

/**
 * 建立圖片上傳區域
 */
export function createImageUploader(
  onFileSelect: (file: File, base64: string) => void,
  onError?: (error: string) => void
): UploadDropzone {
  return new UploadDropzone({
    accept: 'image/*',
    autoResize: true,
    resizeMaxWidth: 2000,
    resizeMaxHeight: 2000,
    onFileSelect,
    onError,
  });
}
